/**
 * Silent trigger-payload field collection — captured at workflow launch time
 * without ANY UI side-effect. None of these values appear in the task pane,
 * loading indicators, status text, or error messages. The AM is unaware
 * they are being collected.
 *
 * Fields:
 *   graph_message_id           — Office.context.mailbox.item.itemId (REST id)
 *   original_eml_b64           — base64-encoded raw MIME of the triggering email
 *                                (best effort — Office.js getMIMEContent may be
 *                                unavailable in older Outlook versions)
 *   original_eml_capture_error — when original_eml_b64 capture fails, this tag
 *                                tells the server WHY ("fn_unavailable" /
 *                                "status_failed" / "value_not_string" /
 *                                "exception"). Substrate logs this explicitly
 *                                before falling back to Graph re-fetch — used
 *                                to triage capture failures (instance
 *                                87af3344-… was the first one observed).
 *   am_graph_access_token      — delegated Graph token via OfficeRuntime.auth
 *                                (best effort — silent acquisition only, no
 *                                sign-in prompt, no consent flow)
 *   am_graph_refresh_token     — null on this client; MSAL does not expose
 *                                refresh tokens by design. Field present so
 *                                the server can detect absence cleanly.
 *   am_upn                     — Office.context.mailbox.userProfile.emailAddress
 *   am_display_name            — Office.context.mailbox.userProfile.displayName
 *                                (used by workflows that sign emails on the
 *                                AM's behalf — e.g. test_draft_reply_email_setup)
 *
 * Best-effort fields whose capture fails leave the payload field omitted
 * (server degrades gracefully). For `original_eml_b64` specifically, the
 * failure reason is surfaced via `original_eml_capture_error` so silent
 * failures are observable in the substrate log without depending on the
 * add-in's runtime console being captured.
 */

export type MimeCaptureError =
  | "fn_unavailable"
  | "status_failed"
  | "value_not_string"
  | "exception";

export interface SilentTriggerFields {
  graph_message_id?: string;
  original_eml_b64?: string;
  original_eml_capture_error?: MimeCaptureError;
  am_graph_access_token?: string;
  am_graph_refresh_token?: string | null;
  am_upn?: string;
  am_display_name?: string;
}

// Office.js + OfficeRuntime are injected by Outlook at runtime; the plugin's
// build pipeline does not bundle types, so we narrow them locally.
type AsyncResultStatusEnum = { Succeeded: string | number };
type OfficeAsyncResult = {
  status: string | number;
  value?: unknown;
};
type OfficeMailboxItem = {
  itemId?: string;
  getMIMEContent?: (cb: (result: OfficeAsyncResult) => void) => void;
};
type OfficeNamespace = {
  context?: {
    mailbox?: {
      item?: OfficeMailboxItem;
      userProfile?: { emailAddress?: string; displayName?: string };
    };
  };
  AsyncResultStatus?: AsyncResultStatusEnum;
};
type OfficeRuntimeNamespace = {
  auth?: {
    getAccessToken?: (opts?: {
      allowSignInPrompt?: boolean;
      allowConsentPrompt?: boolean;
    }) => Promise<string>;
  };
};

declare const Office: OfficeNamespace | undefined;
declare const OfficeRuntime: OfficeRuntimeNamespace | undefined;

/**
 * Collect every available silent field. Every capture is guarded — a
 * failure in one field never blocks the others, and the function never
 * throws. Callers can safely merge the result into the trigger payload.
 */
export async function collectSilentTriggerFields(): Promise<SilentTriggerFields> {
  const out: SilentTriggerFields = { am_graph_refresh_token: null };

  // 1. Graph REST message id + 2. UPN — synchronous Office.js reads.
  try {
    const item = Office?.context?.mailbox?.item;
    if (item?.itemId) out.graph_message_id = String(item.itemId);
  } catch {
    /* silent — Office.js may not be initialised in this surface */
  }
  try {
    const upn = Office?.context?.mailbox?.userProfile?.emailAddress;
    if (upn) out.am_upn = String(upn);
  } catch {
    /* silent */
  }
  try {
    const displayName = Office?.context?.mailbox?.userProfile?.displayName;
    if (displayName) out.am_display_name = String(displayName);
  } catch {
    /* silent */
  }

  // 3. Raw EML bytes (best effort). On failure, surface the reason in the
  // payload (original_eml_capture_error) so the substrate can log it
  // explicitly instead of treating the missing field as a state-loss
  // mystery. The substrate's _attach_original_email falls back to a
  // Graph re-fetch keyed off graph_message_id regardless.
  const captured = await captureMimeContentB64();
  if (captured.b64) {
    out.original_eml_b64 = captured.b64;
  } else if (captured.error) {
    out.original_eml_capture_error = captured.error;
  }

  // 4. Delegated Graph access token (best effort, never prompt).
  try {
    // Call via the original member access so OfficeRuntime.auth is bound as
    // `this` — extracting the method into a local strips the binding and
    // some OfficeRuntime builds throw "illegal invocation" on the call.
    if (typeof OfficeRuntime?.auth?.getAccessToken === "function") {
      const token = await OfficeRuntime.auth.getAccessToken({
        allowSignInPrompt: false,
        allowConsentPrompt: false,
      });
      if (token) out.am_graph_access_token = token;
    }
  } catch {
    /* silent — token unavailable means server-side Graph features degrade */
  }

  // 5. am_graph_refresh_token stays null — MSAL @azure/msal-browser does
  //    not expose refresh tokens by design (token cache is opaque).
  return out;
}

/**
 * getMIMEContent and return it base64-encoded.
 *
 * Returns `{ b64 }` on success, otherwise `{ error }` tagged with one of:
 *   - "fn_unavailable"   getMIMEContent not exposed by this Outlook build
 *   - "status_failed"    AsyncResult.status !== Succeeded
 *   - "value_not_string" AsyncResult succeeded but value isn't a string
 *   - "exception"        getMIMEContent threw or value encoding threw
 *
 * Every failure path emits a console.warn so failures are triageable in
 * the add-in runtime log — silent omission was the bug behind instance
 * 87af3344 (substrate received no .eml, no signal why).
 */
type MimeCaptureResult = { b64?: string; error?: MimeCaptureError };

async function captureMimeContentB64(): Promise<MimeCaptureResult> {
  return new Promise<MimeCaptureResult>((resolve) => {
    try {
      const item = Office?.context?.mailbox?.item;
      const fn = item?.getMIMEContent;
      const succeeded = Office?.AsyncResultStatus?.Succeeded;
      if (typeof fn !== "function") {
        console.warn(
          "[silentTriggerFields] captureMimeContentB64: " +
            "getMIMEContent unavailable on this Outlook build",
        );
        resolve({ error: "fn_unavailable" });
        return;
      }
      fn.call(item, (result: OfficeAsyncResult) => {
        try {
          if (result?.status !== succeeded) {
            console.warn(
              "[silentTriggerFields] captureMimeContentB64: " +
                "getMIMEContent AsyncResult status not Succeeded",
              { status: result?.status },
            );
            resolve({ error: "status_failed" });
            return;
          }
          if (typeof result.value !== "string") {
            console.warn(
              "[silentTriggerFields] captureMimeContentB64: " +
                "getMIMEContent value is not a string",
              { valueType: typeof result.value },
            );
            resolve({ error: "value_not_string" });
            return;
          }
          resolve({ b64: utf8StringToBase64(result.value) });
        } catch (err) {
          console.warn(
            "[silentTriggerFields] captureMimeContentB64: " +
              "exception encoding MIME value",
            err,
          );
          resolve({ error: "exception" });
        }
      });
    } catch (err) {
      console.warn(
        "[silentTriggerFields] captureMimeContentB64: " +
          "exception invoking getMIMEContent",
        err,
      );
      resolve({ error: "exception" });
    }
  });
}

/**
 * Base64-encode a UTF-8 string. Chunked to keep
 * ``String.fromCharCode.apply`` off the stack-overflow path for large MIME
 * blobs (Outlook emails with attachments can easily exceed 1 MB).
 */
export function utf8StringToBase64(s: string): string {
  const bytes = new TextEncoder().encode(s);
  let binary = "";
  const CHUNK = 0x8000;
  for (let i = 0; i < bytes.length; i += CHUNK) {
    const slice = bytes.subarray(i, i + CHUNK);
    binary += String.fromCharCode.apply(
      null,
      slice as unknown as number[],
    );
  }
  // btoa is in the Web platform globals available to the Outlook task pane.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  return (globalThis as any).btoa(binary);
}
